import React, {Fragment, useContext, useEffect, useState} from 'react';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import DatePicker from 'patient-ping-remedy/packages/date-picker';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {sizes} from 'patient-ping-remedy/packages/theme';
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {StyledDisclaimerText}
  from "@manual_encounters/encounter_admit/pings/AdmitHookForm.styles";
import {StyledRow} from "@manual_encounters/encounter_client_search/PatientInformationForm.styles";
import {DatePickerWrapper} from "@common/DatePickerWrapper";
import {log} from "@/helpers/logger.js";
import {EncounterClient} from "@/api/dto/encounter";
import {GenericObject} from "@/hooks/useForm";

const requiredField = 'Required field';

type Props = {
  form: GenericObject;
  handleChange: (e: React.ChangeEvent | null, name: string, value: any) => void;
  patient: EncounterClient;
  isSnf: boolean;
  isEditing: boolean;
  groupId: number;
  isDeState: boolean;
}

const AdmitFormNew = ({ form, handleChange, patient, isSnf, isEditing, groupId, isDeState }: Props) => {
  const [settingOptions, setSettingOptions] = useState<Value[]>([]);
  const [showUpdateDisclaimer, setShowUpdateDisclaimer] = useState(false);
  const { carecoApi } = useContext(CarecoApiContext);

  useEffect(() => {
    carecoApi?.readSettings(groupId).then((options) => {
      setSettingOptions(options);
    });
  }, []);

  useEffect(() => {
    if (showUpdateDisclaimer) {
      const disclaimer = document.getElementById('admitUpdateDisclaimer');
      disclaimer?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showUpdateDisclaimer]);

  const checkDisclaimer = () => {
    setShowUpdateDisclaimer(form.setting.touched);
  };

  const canShowDisclaimer = isSnf && isEditing && !!patient.currentPingEncounter;

  const handleSearch = async (inputValue: string) => {
    try {
      const facilities = await carecoApi?.searchFacilities(inputValue, undefined, groupId);
      const options = facilities?.map<{ label: string, value: string | number }>((facility) => ({
        label: facility.label,
        value: facility.id,
      }));
      if (isDeState) {
        options?.unshift({ label: 'DE Providers', value: 'DE_PROVIDERS' });
      }
      return options;
    } catch (error) {
      log('error', 'Error fetching facilities:', error);
      return [];
    }
  };

  return (
    <Fragment>
      <DropdownList
        id="admittedFrom"
        label="Admitted From"
        value={form.admittedFrom.value?.label ? form.admittedFrom.value : null}
        handleChange={(value: Value | null) => handleChange(null, 'admittedFrom', value)}
        handleSearch={handleSearch}
        placeholder="Search..."
        isSearchable
        required
        error={form.admittedFrom.touched && !form.admittedFrom.valid.valid}
        errorMessage="Required field"
      />
      <Spacer itemHeight={sizes.medium} />
      <StyledRow>
        <DatePickerWrapper>
          <DatePicker
            onChange={(date) => {
              handleChange(null, 'date', date);
              return date;
            }}
            maxDate={new Date()}
            value={form.date.value}
            externalError={form.date.touched && !form.date.valid.valid}
            externalErrorText={requiredField}
            id="admitDate"
            labelText="Admitted Date"
            required="required"
          />
        </DatePickerWrapper>
        {isSnf && (
          <DropdownList
            handleChange={(value: Value | null) => {
              handleChange(null, 'setting', value?.value);
              if (canShowDisclaimer) checkDisclaimer();
            }}
            value={settingOptions.find((option) => option.value === form.setting.value)}
            id="setting"
            items={settingOptions}
            label="Admitted To - Setting"
            isWithinModal
            required
            error={form.setting.touched && !form.setting.valid.valid}
            errorMessage={requiredField}
          />
        )}
      </StyledRow>
      {showUpdateDisclaimer && (
        <StyledDisclaimerText id="admitUpdateDisclaimer">
          Are you trying to transfer this client? Please use the Update Client Status flow instead by clicking on the
          pencil on the client card. Editing the client status here will not produce a Ping for anyone on this
          client's care team.
        </StyledDisclaimerText>
      )}
    </Fragment>
  );
};

export default AdmitFormNew;
